import { useState } from 'react';
import { listUsers, resetPassword, type User } from '../api';
import { message, useAsync } from '../lib/async';
import { Empty, Id, Link, Refused, Result, when } from '../lib/ui';

/**
 * Family-directory users. Staff are not listed here and never have been — a
 * panel that can enumerate and reset other admins is a lateral-movement tool.
 *
 * 501 ON THIS DEPLOYMENT, THE WHOLE SCREEN. `/admin/users` reads a user
 * directory, and shared/directory.py's only implementation is Cognito;
 * admin.py refuses with `{"error": "FAMILY_USER_POOL_ID not configured"}` when
 * the pool id is unset, which it always is on Azure. That is a permanent,
 * correct answer, so `Result` renders it as a stated absence rather than as a
 * red retryable failure.
 *
 * WHAT THIS SCREEN USED TO DO IS WHY IT WAS REWRITTEN. `load()` caught the
 * error into `setError` and left `users` at `null`, and the two were rendered
 * independently — so the screen showed "Could not load this." (with no `what`,
 * so literally that) AND, underneath it, `loading users…` forever. Two
 * contradictory states at once, which is the exact thing lib/async.ts's
 * discriminated union exists to make unrepresentable; this screen was the last
 * one still hand-rolling it.
 */
export function Users({ initialFilter }: { initialFilter?: string | undefined } = {}) {
  const [filter, setFilter] = useState(initialFilter ?? '');
  // What was last SEARCHED, not what is typed. Separate state because this
  // screen submits on a button rather than on every keystroke.
  const [query, setQuery] = useState(initialFilter ?? '');
  const [note, setNote] = useState('');
  /**
   * A refused CHANGE, and it has to carry the cause, not just the words.
   *
   * This used to be a bare string rendered through `Problem`, which is written
   * for a READ: a refused reset produced "Could not load the password reset."
   * and "Nothing has been ruled out — this is a failed call, not an empty
   * result." Every clause of that is false about a mutation the server declined
   * — it did find out, it said no, and nothing was applied — and because the
   * cause was thrown away, none of `Refused`'s discriminators could run. The
   * one that matters here is the role check: this is the screen an operator is
   * on when they email a parent a reset, so a `staff` assignment removed
   * mid-session showed up as a red retryable box instead of the sentence that
   * names entra_bootstrap.sh.
   */
  const [refused, setRefused] = useState<{ error: string; cause: unknown } | null>(null);

  // An options object, not a bare string: /admin/users also takes `limit` and
  // the directory's own `next` pagination token, which is a different issuer
  // from the table cursors every other list uses.
  const { result, reload } = useAsync(
    () => listUsers({ ...(query ? { filter: query } : {}) }),
    [query],
  );

  const reset = async (user: User) => {
    setNote('');
    setRefused(null);
    // Named explicitly: an operator should know they are NOT being given a
    // password to pass on. The wording describes what the API promises rather
    // than naming a provider — the handler reports back what it actually did,
    // and that sentence is what gets shown.
    if (
      !confirm(
        `Start a password reset for ${user.email}?\n\n` +
          `No password is set by staff. The directory sends the user their ` +
          `own reset and only they ever see it.`,
      )
    )
      return;
    try {
      const out = await resetPassword(user.sub);
      setNote(`${user.email}: ${out.detail}`);
    } catch (err) {
      setRefused({ error: message(err), cause: err });
    }
  };

  return (
    <>
      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault();
          setQuery(filter);
          // Same filter twice is still a request to look again.
          if (filter === query) reload();
        }}
      >
        <input
          placeholder="filter by email prefix"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <button>Search</button>
      </form>
      {refused && (
        <Refused what="the password reset" error={refused.error} cause={refused.cause} />
      )}
      {note && <p className="ok">{note}</p>}
      <Result
        of={result}
        what="users"
        retry={reload}
        missing="This build of the staff API has no /admin/users route."
        unavailableDetail={
          <>
            Listing users means reading an identity directory, and there is no
            implementation of that seam on this deployment — shared/directory.py
            has a Cognito lane and nothing else. Until there is one, the way to
            get from an email address to a household is the Families screen. A
            parent who cannot sign in resets their own password through the
            identity provider's pages, not from here.
          </>
        }
      >
        {(page) =>
          !page.users.length ? (
            <Empty what="users" filtered={Boolean(query)} onClear={() => {
              setFilter('');
              setQuery('');
            }} />
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Family</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {page.users.map((u) => (
                  <tr key={u.sub}>
                    <td>
                      {u.email}
                      {!u.emailVerified && <span className="pill"> unverified</span>}
                      {u.enabled === false && <span className="pill pill-warn"> disabled</span>}
                    </td>
                    {/* The whole reason admin.py resolves this. familyId is not
                        the principal's own id, so pasting a sub into
                        /families/<id> 404s — and support always starts from an
                        email address. null is a real answer: signed up, never
                        opened the app. */}
                    <td>
                      {u.familyId ? (
                        <Link to={{ name: 'families', familyId: u.familyId }}>
                          <Id value={u.familyId} />
                        </Link>
                      ) : (
                        <span
                          className="muted"
                          title="no member row — this account has never bootstrapped a family"
                        >
                          none
                        </span>
                      )}
                    </td>
                    <td className="muted">{u.status}</td>
                    <td className="muted">{when(u.createdAt)}</td>
                    <td>
                      <button className="link" onClick={() => void reset(u)}>
                        email a reset
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </Result>
    </>
  );
}
